import React, { useState, useMemo, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Form, Input, Select, Menu, Dropdown, Tooltip } from 'antd';
import { TranslationOutlined, SettingOutlined, DeleteFilled, MoreOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'hooks/apollo';
import useCustomer from 'hooks/useCustomer';

import Page from 'components/Page';
import Loading from 'components/Loading';

import globalMessages from 'translations/messages';
import SurveySelect from 'components/SurveySelect';
import SurveyTitle from 'components/SurveyTitle';
import useGlobalContext from 'hooks/useGlobalContext';
import Table from 'components/Table';
import LanguageSelect from 'components/LanguageSelect';
import messages from './messages';
import surveyQuery from './query.gql';
import updateMutation from './update.gql';
import deleteMutation from './delete.gql';
import disconnectMutation from './disconnect.gql';

import ConnectSurveyModal from './ConnectSurveyModal';

const { Option } = Select;

const getQuestions = parent => parent && parent.children.reduce((rows, { question, block }) => {
  if (question) {
    rows.push({
      ...question,
      children: question.options.length && question.options.map(o => ({ ...o, question })),
    });
  }

  if (block) {
    rows.push(...getQuestions(block));
  }

  return rows;
}, []);

const getInitialValues = survey => {
  const questions = {};
  const options = {};

  if (survey) {
    const initValues = parent => parent && parent.children.forEach(({ question, block }) => {
      if (question) {
        questions[question.id] = question.title;
        question.options.forEach(o => {
          options[o.id] = o.title;
        });
      }
      if (block) {
        initValues(block);
      }
    });

    initValues(survey.root);
  }

  return {
    questions,
    options,
  };
};

// const getBreadcrumbs = (survey) => {
//   const config = {
//     items: [
//       { path: '/surveys', breadcrumbName: <FormattedMessage {...globalMessages.surveys} /> },
//       { path: `/surveys/edit/${survey.id}/settings`, breadcrumbName: survey.title },
//       { path: 'translate', breadcrumbName: <FormattedMessage {...globalMessages.translations} /> },
//     ],
//     itemRender: (route, params, routes) => (
//       routes.indexOf(route) === routes.length - 1
//         ? route.breadcrumbName
//         : <Link to={route.path}>{route.breadcrumbName}</Link>
//     ),
//   };

//   return config;
// };

const LanguageSelectHeader = injectIntl(({ intl: { formatMessage: $t } }) => (
  <Form.Item
    noStyle
    name={['create', 'language']}
    rules={[{ required: true, message: $t(globalMessages.required) }]}
  >
    <LanguageSelect />
  </Form.Item>
));

export const ConnectSelect = ({ isQuestion, options, ...rest }) => {
  let placeholder;

  if (isQuestion) {
    placeholder = messages.connectQuestion;
  } else if (!options) {
    placeholder = messages.connectOptionNoQuestion;
  } else if (!options.length) {
    placeholder = messages.connectOptionNoOptions;
  } else {
    placeholder = messages.connectOption;
  }

  return (
    <Select
      showSearch
      allowClear
      optionFilterProp="children"
      placeholder={<FormattedMessage {...placeholder} />}
      {...rest}
      style={{ width: '100%' }}
    >
      {options?.map(q => <Option key={q.id} value={q.id}>{q.title}</Option>)}
    </Select>
  );
};

const TouchedWrapper = ({ isTouched, children, ...rest }) => <div className={isTouched ? 'touched' : ''}>{React.cloneElement(children, rest)}</div>;

export const renderTranslation = ({ group, record, translations, questions, index = 0, form, onDisconnect, disconnected }) => {
  const isQuestion = !record.question;
  const prop = isQuestion ? 'questions' : 'options';

  const translation = translations[group?.id];
  const isDisconnected = disconnected[prop].includes(translation?.id) || (!isQuestion && disconnected.questions.includes(translations[record.question.group?.id]?.id));

  let name;
  let input;
  let shouldUpdate = () => false;

  if (translation && !isDisconnected) {
    name = ['update', prop, translation.id];
    input = (
      <Input suffix={(
        <Tooltip title={<FormattedMessage {...messages.removeConnection} />}>
          <DisconnectOutlined
            className="ant-btn-dangerous"
            onClick={() => onDisconnect(prop, translation.id)}
          />
        </Tooltip>
      )}
      />
    );
  } else {
    name = ['connect', prop, record.id, index];

    const getOptions = () => {
      if (isQuestion) {
        return questions.filter(q => q.settings.type === record.settings.type);
      }

      // Unsaved relation
      const connectedQuestion = form.getFieldValue(['connect', 'questions', record.question.id, index]);
      if (connectedQuestion) {
        return questions.find(q => q.id === connectedQuestion)?.options;
      }

      return translations[record.question?.group?.id]?.options;
    };

    input = <ConnectSelect isQuestion={isQuestion} options={getOptions()} />;

    if (!isQuestion) {
      // Update select for options if question changes
      shouldUpdate = (prev, curr) => prev.connect?.questions?.[record.question.id] !== curr.connect?.questions?.[record.question.id];
    }
  }

  return (
    <Form.Item noStyle shouldUpdate={shouldUpdate}>
      {() => (
        <Form.Item
          name={name}
          noStyle
        >
          <TouchedWrapper isTouched={form.isFieldTouched(name) || isDisconnected}>
            {input}
          </TouchedWrapper>
        </Form.Item>
      )}
    </Form.Item>
  );
};

const deleteMenuItem = { key: 'delete', label: <FormattedMessage {...globalMessages.delete} />, icon: <DeleteFilled />, danger: true };
const disconnectMenuItem = { key: 'disconnect', label: <FormattedMessage {...messages.disconnect} />, icon: <DisconnectOutlined /> };

const getTranslationColumn = ({ survey, translationSurvey, onSurveyMenuClick, onDisconnect, form, disconnected, setTranslationSurveyId }) => {
  const column = {
    title: (
      <SurveySelect surveys={survey?.languageParent?.children} exclude={[survey?.id]} onChange={setTranslationSurveyId} value={translationSurvey?.id} />
    ),
    width: '50%',
    dataIndex: 'group',
    render: () => null,
  };

  if (translationSurvey) {
    const { id, language, languageName, root } = translationSurvey;
    const questions = getQuestions(root);
    const translations = {};

    questions.forEach(q => {
      if (q.group) {
        translations[q.group.id] = q;
      }
      q.options.forEach(o => {
        if (o.group) {
          translations[o.group.id] = o;
        }
      });
    });

    column.filterIcon = <SettingOutlined />;
    column.filterDropdown = ({ confirm }) => (
      <Menu onClick={({ key }) => { confirm(); onSurveyMenuClick(key, { id, title: `${languageName} (${language})` }); }} items={[deleteMenuItem, disconnectMenuItem]} />
    );
    column.render = (group, record) => renderTranslation({ group, record, translations, questions, form, onDisconnect, disconnected });
  }

  return column;
};

const newTranslationColumn = (onNewColumnClick) => ({
  title: <LanguageSelectHeader />,
  dataIndex: 'id',
  filterIcon: <SettingOutlined />,
  filterDropdown: ({ confirm }) => (
    <Menu onClick={({ key }) => { confirm(); onNewColumnClick(key); }} items={[deleteMenuItem]} />
  ),
  render: (id) => (
    <Form.Item
      name={['create', id]}
      noStyle
    >
      <Input />
    </Form.Item>
  ),
});

const initialDisconnects = { questions: [], options: [] };

const TranslateSurvey = ({ intl: { formatMessage: $t }, surveyId }) => {
  const { customer } = useCustomer();

  const { refetch: refetchSurvey, loading, data: { survey } = {} } = useQuery(surveyQuery, {
    variables: { id: surveyId },
    fetchPolicy: 'network-only',
    skip: !surveyId,
  });

  const [translationSurveyId, setTranslationSurveyId] = useState();

  const { refetch: refetchTranslationSurvey, loading: translationSurveyLoading, data: { survey: translationSurvey } = {} } = useQuery(surveyQuery, {
    variables: { id: translationSurveyId },
    fetchPolicy: 'network-only',
    skip: !translationSurveyId,
  });

  const [updateTranslations, { loading: updating }] = useMutation(updateMutation);
  const [deleteSurvey] = useMutation(deleteMutation);
  const [disconnectSurvey] = useMutation(disconnectMutation);

  const [form] = Form.useForm();

  const [newTranslation, setNewTranslation] = useState(false);
  const [connectSurveyModal, setConnectSurveyModal] = useState(false);
  const [disconnected, setDisconnected] = useState(initialDisconnects);
  const { modal, message } = useGlobalContext();

  const onSurveyMenuClick = (key, { id, title }) => {
    if (key === 'delete') {
      modal.confirm({
        title: $t(globalMessages.confirmDelete, { title }),
        okText: $t(globalMessages.delete),
        okType: 'danger',
        async onOk() {
          await deleteSurvey({ variables: { id } });
          setTranslationSurveyId(null);
          await refetchSurvey();
        },
      });
    }

    if (key === 'disconnect') {
      modal.confirm({
        title: $t(messages.confirmDisconnect, { title }),
        okText: $t(globalMessages.save),
        okType: 'danger',
        async onOk() {
          await disconnectSurvey({ variables: { id } });
          setTranslationSurveyId(null);
          await refetchSurvey();
        },
      });
    }
  };

  const onDisconnect = (prop, id) => {
    setDisconnected({
      ...disconnected,
      [prop]: [
        ...disconnected[prop],
        id,
      ],
    });
  };

  const onNewColumnClick = (key) => {
    if (key === 'delete') {
      setNewTranslation(false);
    }
  };

  const questions = useMemo(() => getQuestions(survey?.root), [survey]);

  const initialValues = useMemo(() => ({ update: getInitialValues(translationSurvey) }), [translationSurvey]);
  const translationColumn = useMemo(() => {
    if (newTranslation) {
      return newTranslationColumn(onNewColumnClick);
    }
    return getTranslationColumn({ survey, translationSurvey, onSurveyMenuClick, onDisconnect, disconnected, form, setTranslationSurveyId });
  }, [survey, translationSurvey, disconnected, newTranslation]);

  useEffect(form.resetFields, [translationSurvey]);

  if (loading || !customer) { return <Loading />; }

  const onFinish = async () => {
    try {
      const values = form.getFieldsValue(null, ({ touched }) => touched);

      const { data: { updateSurveyTranslations } } = await updateTranslations({ variables: {
        input: {
          surveyId,
          ...values,
          disconnect: disconnected,
        },
      } });

      if (updateSurveyTranslations) {
        await refetchSurvey();
        setTranslationSurveyId(updateSurveyTranslations);
      } else {
        await refetchTranslationSurvey();
      }

      setNewTranslation(false);
      setDisconnected(initialDisconnects);

      form.resetFields();

      message.success('Translations updated!');
    } catch (e) {
      message.error(e.message);
    }
  };

  const columns = [
    {
      title: <SurveyTitle survey={survey} />,
      dataIndex: 'title',
      width: '50%',
    },
    translationColumn,
  ];

  const getHeaderActions = () => {
    const menuItems = [{
      key: 'connect',
      label: <FormattedMessage {...messages.connectExistingSurvey} />,
      onClick: () => setConnectSurveyModal(true),
    }];

    return [
      <Button key="primary" type="primary" disabled={!survey} onClick={() => setNewTranslation(true)}>
        <TranslationOutlined />
        <FormattedMessage {...messages.addTranslation} />
      </Button>,
      <Dropdown key="more" placement="bottomRight" trigger={['click']} menu={{ items: menuItems }}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>,
    ];
  };

  return (
    <Form form={form} onFinish={onFinish} initialValues={initialValues}>
      <Page>
        <Page.Header title={survey.title} extra={getHeaderActions(survey)} />
        <Page.Content className="translate-survey-content">
          <ConnectSurveyModal customerId={customer.id} surveyId={surveyId} surveyGroupId={survey.languageParent?.id} open={connectSurveyModal} onCancel={() => setConnectSurveyModal(false)} onDone={() => { setConnectSurveyModal(false); refetchSurvey(); }} />

          <Table
            columns={columns}
            dataSource={questions}
            pagination={false}
            bordered
            expandable={{
              defaultExpandAllRows: true,
              expandIcon: () => null,
            }}
            size="small"
            loading={translationSurveyLoading}
            interactive={false}
            resizable={false}
          />

          <Page.Footer sticky>
            <Form.Item
              noStyle
              shouldUpdate
            >
              {({ isFieldsTouched }) => (
                <Button type="primary" onClick={form.submit} loading={loading || updating} disabled={!(isFieldsTouched() || disconnected.questions.length || disconnected.options.length)}>
                  <FormattedMessage {...globalMessages.save} />
                </Button>
              )}
            </Form.Item>
          </Page.Footer>
        </Page.Content>
      </Page>
    </Form>
  );
};

export default injectIntl(TranslateSurvey);
